import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import Image from 'next/image';
import Button from '$/components/base/Button';
import HeadLogo from '$/components/HeadLogo';
import IconFont from '$/components/IconFont';
import Tooltip from '$/components/Tooltip';
import useCreateRoom from '$/hooks/useCreateRoom';
import { useLanguageStore } from '$/store/languageStore';
import { useOfficialStore } from '$/store/useOfficialStore';
import useThemeStore from '$/store/useThemeStore';

import { mtstatTrack } from '$/utils/mtstat';
import { getEnv, navigate } from '@/common/agents';
import { getCookieHomeRoute, getCookieHomeUrl, getHomeVersionCookie } from '@/common/homeVersionCookie';
import { useCommonStore, setNavbarExpand } from '@/store/useCommonStore';
import useTeamStore from '@/store/useTeamStore';
import { createNewFlow, createNewCanvas } from '@/utils/createRoom';
import type { PageInfo } from '@/common/pageType';
import { AI_TOOLS } from '@/domains/tools/constant';
import { getLocalizedAiToolRoute } from '@/domains/tools/getLocalizedAiToolRoute';
import COLLAPSE_LOGO from '../../assets/collapse-logo.png';
import COLLAPSE_LOGO_BLACK from '../../assets/collapse-logo-black.png';
import MODE_1 from './mode-1.png';
import MODE_2 from './mode-2.png';
import MODE_3 from './mode-3.png';
import styles from './index.module.scss';
import NavbarAssetStorageCard from './NavbarAssetStorageCard';

export { createNewCanvas, createNewFlow, createNewAgentTeams } from '@/utils/createRoom';

export enum HomeBar {
  Home = 'home',
  AgentShop = 'agent_shop',
  Community = 'community',
  Project = 'project',
  DataDashboard = 'data_dashboard',
  TeamProject = 'team_project',
  Asset = 'asset',
}

const NAVBAR_TRACK_LOCATION_MAP: Record<PageInfo['locationName'], string> = {
  home: 'home_page',
  project: 'project_page',
  team_project: 'team_project_page',
  community: 'community_page',
  communityDetail: 'community_detail_page',
  agent_shop: 'agent_shop_page',
  asset: 'asset_page',
  case_detail: 'case_detail_page',
  studio: 'room_detail',
  ai_flow: 'workflow_page',
  social_case: 'social_case_page',
  data_dashboard: 'data_dashboard_page',
};

// 侧边栏先隐藏视频剪辑入口，首页工具区仍沿用完整 AI_TOOLS 列表。
const SIDEBAR_AI_TOOLS = AI_TOOLS.filter((tool) => tool.id !== 'ai_video');

/** 获取侧边栏展开收起埋点的页面位置。 */
function getNavbarTrackLocation(activeNav: PageInfo['locationName']) {
  return NAVBAR_TRACK_LOCATION_MAP[activeNav] || '';
}

/** 从当前 URL 读取房间 id，非房间页面兜底为空字符串。 */
function getCurrentRoomId() {
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get('room_id') || '';
}

interface NavbarProps {
  activeNav: PageInfo['locationName'];
  setActiveNav: (nav: PageInfo['locationName']) => void;
}

// 首页侧边栏仅调整尺寸与间距，导航数据、展示顺序和跳转逻辑保持不变。
function Navbar({ activeNav, setActiveNav }: NavbarProps) {
  const theme = useThemeStore((state) => state.theme);
  const languageStore = useLanguageStore((state) => state.languageStore);
  const expand = useCommonStore((state) => state.navbarExpand);
  const isUserLoggedIn = useOfficialStore((state) => state.isUserLoggedIn);
  const toLogin = useCommonStore((state) => state.toLogin);
  const initUserInfo = useCommonStore((state) => state.initUserInfo);
  const isTeam = useTeamStore((state) => Boolean(state.teamInfo?.oid));
  // 退出登录会清空团队信息，团队项目路由仍需保留对应入口与选中态。
  const showTeamProjectEntry = isTeam || activeNav === HomeBar.TeamProject;
  const { createRoom: createAgentTeamRoom } = useCreateRoom({
    onLoginSuccessed: async () => {
      await initUserInfo();
    },
    createAfterLogin: true,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [aiToolsExpanded, setAiToolsExpanded] = useState(false);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--sidebar-width', expand ? '88px' : '264px');
  }, [expand]);

  const BarList = [
    {
      id: HomeBar.Home,
      name: languageStore.smartcatTranslationText(
        (v) => v.roboneo_web_home_page,
      ),
      icon: 'homeV4',
    },

    // 作品社区，agentteam需求产品让暂时隐藏 https://cf.meitu.com/confluence/pages/viewpage.action?pageId=675242827
    // {
    //   id: HomeBar.Community,
    //   name: languageStore.smartcatTranslationText(
    //     (v) => v.roboneo_web_works_community,
    //   ),
    //   icon: 'planet',
    // },

    showTeamProjectEntry
      ? {
        id: HomeBar.TeamProject,
        name: languageStore.smartcatTranslationText((v) => v.team_project),
        icon: 'userGroupV3Bold',
      }
      : {
        // 个人态继续展示原有的“我的项目”入口。
        id: HomeBar.Project,
        name: languageStore.smartcatTranslationText(
          (v) => v.roboneo_web_my_projects,
        ),
        icon: 'clock',
      },
          // 短剧洞察
    {
      id: HomeBar.DataDashboard,
      name: languageStore.smartcatTranslationText(
        (v) => v.robo_short_drama_insights,
      ),
      icon: 'chart',
    },
    // 资产库
    // {
    //   id: HomeBar.Asset,
    //   name: languageStore.smartcatTranslationText(
    //     (v) => v.asset_library,
    //   ),
    //   icon: 'folderOpen',
    // },
    // agent 广场
    // {
    //   id: HomeBar.AgentShop,
    //   name: languageStore.smartcatTranslationText(
    //     (v) => v.roboneo_agent_shop,
    //   ),
    //   icon: 'AIDialogue',
    // },
  ];

  /** 点击侧栏 Logo 回首页时，沿用 cookie 中选择的新旧首页版本。 */
  const goHomepage = () => {
    navigate({
      mode: 'url',
      url: getCookieHomeUrl(getEnv().ENV === 'development'),
    });
  };

  /** 点击侧栏 AI 工具入口，按当前语言上报后跳转到 Essio 对应落地页。 */
  const aiToolClicked = (tool: any) => {
    mtstatTrack('roboneo_aifunc_enter', {
      func: tool.id,
      source: 'side_bar',
    });
    navigate({
      mode: 'router',
      pathname: getLocalizedAiToolRoute(tool.route, languageStore.languageCode) as any,
    });
  };

  /** 点击侧栏主导航时，Home tab 按 cookie 中选择的新旧首页版本跳转。 */
  const goTabItem = (id: HomeBar) => {
    // 重复点击不需要跳转
    if (activeNav === id) return;
    window.mtstat?.track('home_page_guide_click', {
      icon_name: id,
    });
    if (id === 'home') {
      window.scrollTo({ top: 0 });
    }

    // 资产库、我的创作需登录：先调起登录；成功后再跳转对应页并高亮 tab（资料拉取失败不阻塞）
    if (
      (id === HomeBar.Asset || id === HomeBar.Project || id === HomeBar.TeamProject)
      && !isUserLoggedIn()
    ) {
      toLogin()
        .then(async () => {
          if (!useOfficialStore.getState().isUserLoggedIn()) return;
          try {
            await initUserInfo();
          } catch {
            // 资料失败不阻塞进入目标页
          }
          navigate({
            mode: 'router',
            pathname: `/--roboneo/${id}`,
          });
          setActiveNav(id);
        })
        .catch(() => {});
      return;
    }

    // 重复点击不需要跳转
    if (activeNav === id) return;

    navigate({
      mode: 'router',
      pathname: id === HomeBar.Home ? getCookieHomeRoute() : `/--roboneo/${id}`,
    });
    setActiveNav(id);
  };

  /** 切换侧边栏展开状态，同时上报当前页面位置。 */
  const handleNavbarExpandChange = (nextExpand: boolean) => {
    mtstatTrack('roboneo_roomtop_unfold_click', {
      room_id: getCurrentRoomId(),
      type: nextExpand ? 'close' : 'open',
      location: getNavbarTrackLocation(activeNav),
    });
    setNavbarExpand(nextExpand);
  };

  /**
   * 直接创建侧边栏 AgentTeam 房间，供新首页和旧首页弹窗选中 Agent Teams 时复用。
   */
  const createSidebarAgentTeamRoom = () => {
    createAgentTeamRoom({
      trackLocation: isTeam ? 'team_add' : 'home_page_bar',
      trackType: 'agent_teams',
    });
  };

  /**
   * 侧边栏新建入口：旧首页先选择模式，新首页直接进入 AgentTeam。
   */
  const handleCreateAgentTeamRoom = () => {
    if (getHomeVersionCookie() !== 'old') {
      createSidebarAgentTeamRoom();
    } else {
      setIsModalOpen(true);
    }
  };

  return (
    <div className={styles.navbar_container}>
      {/* 收起 */}
      <div
        className={styles.collapse_navbar_box}
        style={{
          opacity: expand ? '1' : '0',
          zIndex: expand ? 2 : 1,
          pointerEvents: expand ? 'auto' : 'none',
        }}
      >
        {/* logo */}
        <div className={styles.logo_img}>
          <Image
            src={theme === 'light' ? COLLAPSE_LOGO_BLACK : COLLAPSE_LOGO}
            alt="RoboNeo"
            width={28}
          />
        </div>

        {/* 展开按钮 */}
        <Tooltip title={languageStore.smartcatTranslationText((v) => v.roboneo_web_expand_sidebar)} placement="right">
          <Button type="transparent" className={classNames(styles.btn, styles.expand_btn)} onClick={() => handleNavbarExpandChange(!expand)}>
            <IconFont
              name="sidebarLeftSquareBold"
              color="var(--content-buttonSecondary)"
              fontSize={20}
            />
          </Button>
        </Tooltip>

        {/* 新建 */}
        <Tooltip title={languageStore.smartcatTranslationText((v) => v.roboneo_web_project)} placement="right">
          <Button type="transparent" className={classNames(styles.btn, styles.create_btn)} onClick={handleCreateAgentTeamRoom}>
            <IconFont
              name="plusCircleFill"
              color="var(--content-primary)"
              fontSize={20}
            />
          </Button>
        </Tooltip>

        {/* 主导航 */}
        {BarList.map((item) => (
          <Tooltip key={item.id} title={item.name} placement="right">
            <Button
              type="transparent"
              className={classNames(styles.btn, { [styles.active]: activeNav === item.id })}
              onClick={() => goTabItem(item.id)}
            >
              <IconFont
                name={item.icon}
                color="var(--content-primary)"
                fontSize={20}
              />
            </Button>
          </Tooltip>
        ))}

        {/* AI创作工具：与展开态共用 aiToolsExpanded，并展示同步的 chevron */}
        <div className={styles.aiToolsCollapsedSection}>
          <Tooltip
            title={
              aiToolsExpanded
                ? languageStore.smartcatTranslationText((v) => v.roboneo_web_collapse)
                : languageStore.smartcatTranslationText((v) => v.ai_create_tools)
            }
            placement="right"
          >
            <Button
              type="transparent"
              className={classNames(styles.btn, styles.aiToolsCollapsedHeaderBtn, {
                [styles.active]:
                  aiToolsExpanded
                  || SIDEBAR_AI_TOOLS.some((tool) => activeNav === tool.id),
              })}
              onClick={() => setAiToolsExpanded((v) => !v)}
            >
              <span className={styles.aiToolsCollapsedHeaderInner}>
                <IconFont
                  name="magicWand"
                  color="var(--content-primary)"
                  fontSize={20}
                  className={styles.aiToolsCollapsedWand}
                />
                <span className={styles.aiToolsCollapsedHoverToggle} aria-hidden>
                  <IconFont
                    name="chevronDownFill"
                    color="var(--content-primary)"
                    fontSize={20}
                    className={classNames(styles.aiToolsCollapsedHoverChevron, {
                      [styles.aiToolsCollapsedHoverChevronCollapse]: aiToolsExpanded,
                    })}
                  />
                </span>
              </span>
            </Button>
          </Tooltip>
          {aiToolsExpanded
            && SIDEBAR_AI_TOOLS.map((tool: any) => (
              <Tooltip key={tool.id} title={languageStore.smartcatTranslationText((v) => v[tool.title as keyof typeof v])} placement="right">
                <Button
                  type="transparent"
                  className={classNames(styles.btn, {
                    [styles.active]: activeNav === tool.id,
                  })}
                  onClick={() => {
                    aiToolClicked(tool);
                  }}
                >
                  <IconFont
                    name={tool.icon}
                    color="var(--content-primary)"
                    fontSize={20}
                  />
                </Button>
              </Tooltip>
            ))}
        </div>
        {activeNav === HomeBar.Home && (
          <NavbarAssetStorageCard
            className={styles.collapsedAssetStorageCard}
            variant="compact"
          />
        )}
      </div>

      {/* 展开 */}
      <div
        className={styles.navbarBox}
        style={{
          opacity: !expand ? '1' : '0',
          zIndex: !expand ? 2 : 1,
          pointerEvents: !expand ? 'auto' : 'none',
        }}
      >
        {/* 顶部logo */}
        <div className={styles.sidebarHeader}>
          <div
            className={styles.logoBox}
            onClick={goHomepage}
            style={{
              filter: theme === 'light' ? 'brightness(0)' : 'none',
            }}
          >
            <HeadLogo className={styles.logo} />
          </div>
          <Tooltip
            title={languageStore.smartcatTranslationText((v) => v.roboneo_web_collapse_sidebar)}
            placement="right"
          >
            <div className={styles.collapseBox}>
              <IconFont
                className={styles.collapse}
                name="sidebarLeftSquareBold"
                color="var(--content-buttonSecondary)"
                fontSize={16}
                onClick={() => handleNavbarExpandChange(!expand)}
              />
            </div>
          </Tooltip>
        </div>

        <div className={styles.navBox}>
          <Button
            type="transparent"
            className={classNames(styles.nav_btn, styles.create_btn)}
            leftIcon={<IconFont name="plusCircleFill" fontSize={18} className={styles.icon} />}
            onClick={handleCreateAgentTeamRoom}
          >
            {/* 新建项目 */}
            {languageStore.smartcatTranslationText((v) => v.roboneo_web_project)}
          </Button>
          {BarList.map((item) => (
            <Button
              key={item.id}
              type="transparent"
              className={classNames(styles.nav_btn, { [styles.active]: activeNav === item.id })}
              leftIcon={<IconFont name={item.icon} fontSize={18} className={styles.icon} />}
              onClick={() => goTabItem(item.id)}
            >
              {item.name}
            </Button>
          ))}
          {/* AI创作工具 可展开列表 */}
          <div className={styles.aiToolsSection}>
            <div
              className={styles.aiToolsHeader}
              onClick={() => setAiToolsExpanded((v) => !v)}
            >
              <div className={styles.aiToolsHeaderLeft}>
                <IconFont name="magicWand" fontSize={18} className={styles.icon} color="var(--content-primary)" />
                <span className={styles.aiToolsTitle}>{languageStore.smartcatTranslationText((v) => v.ai_create_tools)}</span>
              </div>
              <IconFont
                name="chevronDownFill"
                fontSize={12}
                className={classNames(styles.aiToolsChevron, { [styles.aiToolsChevronExpanded]: aiToolsExpanded })}
                color="var(--content-primary)"
              />
            </div>
            <div className={classNames(styles.aiToolsList, { [styles.aiToolsListExpanded]: aiToolsExpanded })}>
              {SIDEBAR_AI_TOOLS.map((tool: any) => (
                <div
                  key={tool.id}
                  className={classNames(styles.aiToolItem, { [styles.active]: activeNav === tool.id })}
                  onClick={() => { aiToolClicked(tool); }}
                >
                  <IconFont name={tool.icon} fontSize={18} className={styles.icon} color="var(--content-primary)" />
                  <span className={styles.aiToolItemName}>{languageStore.smartcatTranslationText((v) => v[tool.title as keyof typeof v])}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        {activeNav === HomeBar.Home && (
          <NavbarAssetStorageCard
            className={styles.homeAssetStorageCard}
            visible={!expand}
          />
        )}
      </div>

      {/* 创建模式弹窗 */}
      <ChooseCreateModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onChoose={(icon_name) => {
          window.mtstat?.track('home_page_guide_click', { icon_name });
          switch (icon_name) {
            case 'canvas_create':
              createNewCanvas('home_page_bar');
              break;
            case 'workflow_create':
              createNewFlow('home_page_bar');
              break;

            case 'team_studio_create':
              createSidebarAgentTeamRoom();
              break;

            default:
              break;
          }
        }}
      />
    </div>
  );
}

interface ChooseCreateModalProps {
  open: boolean;
  onClose: () => void;
  onChoose: (id: MODE_ID) => void;
}

type MODE_ID = 'canvas_create' | 'workflow_create' | 'team_studio_create';

function ChooseCreateModal({
  open,
  onClose,
  onChoose,
}: ChooseCreateModalProps) {
  const languageStore = useLanguageStore((state) => state.languageStore);
  const maskRef = useRef<HTMLDivElement | null>(null);
  const [mode, setMode] = useState<MODE_ID | null>(null);
  const modeList = [
    {
      id: 'team_studio_create' as MODE_ID,
      title: languageStore.smartcatTranslationText((v) => v.roboneo_agent_teams), // 'roboneo_agent_teams',
      subTitle: languageStore.smartcatTranslationText((v) => v.multiple_agents_work_together_to_deliver_like_a_team), // 多个 Agent 协同，像团队一样分工交付
      logo: MODE_3,
      isNew: true,
      features: [{
        text: languageStore.smartcatTranslationText((v) => v.multi_agent_collaboration), // '多Agent协作'
        icon: 'AIDialogueBold',
      }, {
        text: languageStore.smartcatTranslationText((v) => v.division_of_professional_roles), // '专业角色分工'
        icon: 'shapeBold',
      }, {
        text: languageStore.smartcatTranslationText((v) => v.complex_tasks_are_more_suitable), // '复杂任务更适合'
        icon: 'brainBold',
      }, {
        text: languageStore.smartcatTranslationText((v) => v.multi_step_relay_completion), // '多步骤接力完成'
        icon: 'infiniteFlowBold',
      }, {
        text: languageStore.smartcatTranslationText((v) => v.the_result_is_more_complete), // '结果更完整'
        icon: 'essayBold',
      }, {
        text: languageStore.smartcatTranslationText((v) => v.advanced_user_friendly), // '高阶用户友好'
        icon: 'fourPointedStarBold',
      }, {
        text: languageStore.smartcatTranslationText((v) => v.efficiency_and_quality_are_taken_into_account), // '效率与质量兼顾'
        icon: 'lightBulbV3Bold',
      }, {
        text: languageStore.smartcatTranslationText((v) => v.team_based_creative_experience), // '团队式创作体验'
        icon: 'AIDialogueV2Bold',
      }],
    }, {
      id: 'canvas_create' as MODE_ID,
      title: languageStore.smartcatTranslationText((v) => v.roboneo_web_agent_infinite_canvas), // 'Agent + 无限画布',
      subTitle: languageStore.smartcatTranslationText((v) => v.roboneo_web_chat_create_unlimited), // '释放 AI 协作力，自由创作无界限',
      logo: MODE_1,
      features: [{
        text: languageStore.smartcatTranslationText((v) => v.roboneo_web_pro_imaging_agent), // '专业影像Agent',
        icon: 'paperAirplaneBold',
      }, {
        text: languageStore.smartcatTranslationText((v) => v.roboneo_web_beginner_friendly), // '新手友好'
        icon: 'lightBulbWithLightBold',
      }, {
        text: languageStore.smartcatTranslationText((v) => v.roboneo_web_infinite_canvas), // '无限画布'
        icon: 'frameWithStarBold',
      }, {
        text: languageStore.smartcatTranslationText((v) => v.roboneo_web_chat_based_creation), // '对话式创作'
        icon: 'bubbleV2Bold',
      }, {
        text: languageStore.smartcatTranslationText((v) => v.roboneo_web_realtime_canvas_editing), // '画布实时编辑'
        icon: 'cursorBold',
      }, {
        text: languageStore.smartcatTranslationText((v) => v.roboneo_web_free_creation), // '自由创作'
        icon: 'magicWandBold',
      }, {
        text: languageStore.smartcatTranslationText((v) => v.roboneo_web_task_decomposition), // '智能拆解任务'
        icon: 'nucleusBold',
      }],
    },
    {
      id: 'workflow_create' as MODE_ID,
      title: languageStore.smartcatTranslationText((v) => v.roboneo_web_workflow), // '工作流',
      subTitle: languageStore.smartcatTranslationText((v) => v.roboneo_web_structured_creation), // 创作流程化，灵感有序展开,
      logo: MODE_2,
      features: [{
        text: languageStore.smartcatTranslationText((v) => v.roboneo_web_AgtToWf), // '多节点协作'
        icon: 'pictureBatchBold',
      }, {
        text: languageStore.smartcatTranslationText((v) => v.roboneo_web_advanced_user), // '进阶用户'
        icon: 'pencil',
      }, {
        text: languageStore.smartcatTranslationText((v) => v.roboneo_web_multi_node_collaboration), // '多节点协作'
        icon: 'newWorkFlowBold',
      }, {
        text: languageStore.smartcatTranslationText((v) => v.roboneo_web_reusable_workflow), // '流程可复'
        icon: 'duplicateBold',
      }, {
        text: languageStore.smartcatTranslationText((v) => v.roboneo_web_stable_results), // '结果稳定'
        icon: 'AIThumbnailBold',
      }, {
        text: languageStore.smartcatTranslationText((v) => v.roboneo_web_highly_controllable), // '可控性强'
        icon: 'touchBold',
      }, {
        text: languageStore.smartcatTranslationText((v) => v.roboneo_web_publish_to_community), // '发布到社'
        icon: 'planet',
      }],
    }];

  const _onClose = () => {
    maskRef.current?.classList.remove(styles.open);
  };

  const onTransitionEnd = (e: React.TransitionEvent) => {
    if (e.propertyName === 'opacity' && !maskRef.current?.classList.contains(styles.open)) {
      onClose();
    }
  };

  // 切换路由自动关闭弹窗
  const currentPathname = useCommonStore((state) => state.currentPathname);
  useEffect(() => {
    _onClose?.();
  }, [currentPathname]);

  useEffect(() => {
    requestAnimationFrame(() => {
      if (open) {
        maskRef.current?.classList.add(styles.open);
      }
    });
  }, [open]);

  if (!open) return null;
  return ReactDOM.createPortal(
    <div ref={maskRef} className={classNames(styles.mask_box)} onClick={_onClose} onTransitionEnd={onTransitionEnd}>
      <div className={styles.inner_box}>
        <div className={styles.title}>
          {/* 选择模式 */}
          {languageStore.smartcatTranslationText((v) => v.roboneo_web_select_mode)}
        </div>
        <div className={styles.sub_title}>
          {/* 找到适合你的方式，开启灵感之旅 */}
          {languageStore.smartcatTranslationText((v) => v.roboneo_web_start_inspiration_journey)}
        </div>
        <div className={styles.choose_mode_box} onClick={(e) => e.stopPropagation()}>
          {modeList.map((item) => (
            <div
              key={item.id}
              className={classNames(styles.mode_item, { [styles.active]: item.id === mode })}
              onClick={() => {
                setMode(item.id);
                onChoose(item.id);
              }}
            >
              <div className={styles.active_icon}>
                <IconFont name="checkBoxMark" color="var(--content-buttonPrimary)" fontSize={10} />
              </div>
              <div className={styles.header}>
                <div className={styles.mode_logo}>
                  <Image src={item.logo} alt="mode_icon" />
                </div>
                <div className={styles.right_box}>
                  <div className={styles.title}>
                    {item.title}
                    {item.isNew && <div className={styles.new_tag}>New</div>}
                  </div>
                  <div className={styles.sub_title}>
                    {item.subTitle}
                  </div>
                </div>
              </div>

              {/* 分割线 */}
              <div className={styles.line} />

              <div className={styles.mode_features_title}>
                {/* 模式特点 */}
                {languageStore.smartcatTranslationText((v) => v.roboneo_web_mode_features)}
              </div>
              <div className={styles.features_box}>
                {item.features.map((feature) => (
                  <div key={feature.text} className={styles.features_item}>
                    <IconFont name={feature.icon} color="var(--content-secondary)" fontSize={20} className={styles.icon} />
                    {feature.text}
                  </div>
                ))}

              </div>
            </div>
          ))}

        </div>

        <div
          className={styles.btn_group}
        >
          <Button
            type="plain"
            className={styles.left_btn}
            leftIcon={
              <IconFont name="chevronLeftBlack" color="var(--content-buttonSecondary)" fontSize={17} />
          }
            onClick={() => {
              navigate({
                mode: 'router',
                pathname: getCookieHomeRoute(),
              });
            }}
          >
            {/* 返回首页 */}
            {languageStore.smartcatTranslationText((v) => v.roboneo_web_go_back_home)}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default Navbar;
